import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { StepIndicator } from '@uhg-abyss/web/ui/StepIndicator';
import { Button } from '@uhg-abyss/web/ui/Button';
import { useSteps } from '@src/state/steps';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useLocation } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { event as TaggingEvent } from '@uhg-abyss/web/tools/event';
import { isCliaValid } from '../../../../state/tinRegistration/utils';

const JumpLinkTextStyle = styled('p', {
  fontSize: '16px',
  fontWeight: 'bold',
});

const steps = [
  { name: 'W9Upload', label: 'W-9 Details', route: '/tin-registration/step/1' },
  {
    name: 'PracticeInformation',
    label: 'Practice data',
    route: '/tin-registration/step/2',
  },
  { name: 'Locations', label: 'Locations', route: '/tin-registration/step/3' },
  {
    name: 'TinReview',
    label: 'Attestation',
    route: '/tin-registration/step/4',
  },
];

export const StepTracker = () => {
  const { pathname } = useLocation();

  const router = useRouter();
  const form = useForm();
  const practiceType = form.getValues(
    'practiceInformation.practiceTypeSelection'
  );
  const medicareId = form.getValues('practiceInformation.medicareId');
  const {
    pagesVisited = [],
    pagesSubmitted = [],
    removeSubmittedPage,
  } = useSteps();

  const currentStepIndex =
    steps.findIndex((step) => {
      return pathname.includes(step.route);
    }) + 1;

  const stepCompletionState = steps.reduce((acc, step, idx) => {
    acc[idx + 1] = pagesSubmitted.includes(step.name);
    return acc;
  }, {});

  const jumpLinks = (navigateTo) => {
    let model = '';
    if (navigateTo.includes('/1')) {
      model = 'w9form';
    } else if (navigateTo.includes('/2')) {
      model = 'practiceInformation';
      form.validate('practiceInformation', (values) => {
        let emptyValue = false;
        Object.keys(values).forEach((key) => {
          if (!values[key] && !emptyValue) {
            if (
              key === 'restrictionInput' &&
              values.restrictionSelection === 'No'
            ) {
              emptyValue = false;
            } else if (key === 'medicareAttestation' || key === 'medicareId') {
              if (
                practiceType &&
                practiceType.value === 'Ambulatory Surgery Center'
              ) {
                if (!medicareId && !values.medicareAttestation) {
                  emptyValue = true;
                } else {
                  emptyValue = false;
                }
              } else {
                emptyValue = false;
              }
            } else if (
              key === 'cliaFile' ||
              key === 'cliaIdentification' ||
              key === 'cliaAttestation'
            ) {
              emptyValue = isCliaValid(values);
            } else if (key === 'isNpiValid') {
              emptyValue = false;
            } else {
              emptyValue = true;
            }
          }
        });
        if (emptyValue) {
          const updatedSubmittedList = pagesSubmitted.filter((item) => {
            return item !== 'PracticeInformation';
          });
          removeSubmittedPage(updatedSubmittedList);
        }
      });
    } else if (navigateTo.includes('/3')) {
      model = 'address';
      form.validate('address', (values) => {
        let emptyValue = false;
        Object.keys(values.primaryServicing).forEach((key) => {
          if (!values.primaryServicing[key] && key !== 'streetLine2') {
            emptyValue = true;
          }
        });
        Object.keys(values.primaryBilling).forEach((key) => {
          if (!values.primaryBilling[key] && key !== 'streetLine2') {
            emptyValue = true;
          }
        });
        Object.keys(values.primaryRemittance).forEach((key) => {
          if (!values.primaryRemittance[key] && key !== 'streetLine2') {
            emptyValue = true;
          }
        });
        Object.keys(values).forEach((key) => {
          if (!values[key] && key !== 'secondaryServicing') {
            emptyValue = true;
          }
          if (key === 'secondaryServicing') {
            Object.values(values.secondaryServicing).forEach((value) => {
              Object.keys(value).forEach((keyValue) => {
                if (!value[keyValue] && keyValue !== 'streetLine2') {
                  emptyValue = true;
                }
              });
            });
          }
        });
        if (emptyValue) {
          const updatedSubmittedList = pagesSubmitted.filter((item) => {
            return item !== 'Locations';
          });
          removeSubmittedPage(updatedSubmittedList);
        }
      });
    }
    const navigate = navigateTo.match(/[1-4]/);
    if (navigate && navigate.input) {
      let interactionName = '';
      let interactionValue = '';

      if (navigate.input.includes('/1')) {
        interactionName = 'Tin registration flow timeline';
        interactionValue = 'w9 details widget';
      } else if (navigate.input.includes('/2')) {
        interactionName = 'Tin registration flow timeline';
        interactionValue = 'practice data widget';
      } else if (navigate.input.includes('/3')) {
        interactionName = 'Tin registration flow timeline';
        interactionValue = 'locations widget';
      } else if (navigate.input.includes('/4')) {
        interactionName = 'Tin registration flow timeline';
        interactionValue = 'attestation widget';
      }

      TaggingEvent('ATTESTATION_PAGE_EDIT', {
        interactionName,
        interactionValue,
      });
      const eventData = {
        action: 'StepTracker',
      };
      TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
    }
    form.validate(model, (values) => {
      router.navigate(navigateTo);
    });
  };

  return (
    <StepIndicator
      currentStep={currentStepIndex}
      stepCompletionState={stepCompletionState}
      onStepClick={(stepNum) => {
        const step = steps[stepNum - 1];
        if (pagesVisited.includes(step.name)) {
          jumpLinks(step.route);
        }
      }}
      stepSize="20px"
    >
      {steps.map((step, idx) => {
        const isVisited = pagesVisited.includes(step.name);
        const isCurrent = currentStepIndex === idx + 1;
        const isAllSubmitted = pagesSubmitted.length === steps.length;

        const label =
          isCurrent || !isVisited || isAllSubmitted ? (
            <JumpLinkTextStyle>{step.label}</JumpLinkTextStyle>
          ) : (
            <Button
              variant="tertiary"
              data-testid={`${step.name}-jumplink`}
              onClick={(e) => {
                e.stopPropagation();
                jumpLinks(step.route);
              }}
            >
              {step.label}
            </Button>
          );

        return <StepIndicator.Step key={step.route} label={label} />;
      })}
    </StepIndicator>
  );
};
